/**
 * Card action handler for F8 chat cards.
 * Mirrors app/src/utils/cardActionHandler.js, with web-specific navigation.
 *
 * Returns:
 *   - { type: 'skill_invoke', message } when the card asks to invoke a follow-up
 *     skill via a chat message — caller is expected to forward this to sendMessage.
 *   - { type: 'navigate', path } for navigation actions — caller should navigate.
 *   - null otherwise (handled internally or unsupported).
 */

import { httpsCallable } from 'firebase/functions';
import { FIREBASE_FUNCTIONS } from '../firebaseConfig';

const ALLOWED_CLOUD_FUNCTIONS = new Set<string>([
  'sendUserChatMessageEurW',
  'joinCircleRequestEurW',
  'rsvpCircleEventEurW',
  // PPL-26 — mediation callables card actions may invoke. Destructive
  // exit verbs (end/pause/delete) are intentionally omitted; those route
  // through a page confirmation, not a one-shot card tap.
  'createMediationInviteEurW',
  'respondToMediationInviteEurW',
  'acceptMediationInviteByTokenEurW',
  'setMediationReadyEurW',
  'submitMediationCheckinEurW',
  'requestMediatorTurnEurW',
  // PPL-335 — the person_chooser card answers "which Sarah?" by accepting the
  // suggestion against the chosen person. Only the suggestion verbs: the undo
  // path is terminal and routes through the review page, not a card tap.
  'respondToSuggestionEurW',
]);

const ALLOWED_SCREENS = new Set<string>([
  'UserChatScreen', 'UserChat', 'PublicUserProfile',
  'CircleDetail', 'CircleChat', 'CircleEventDetail', 'CircleEvents', 'CirclesTab',
  'Conversation', 'PplTalk', 'Insights', 'UserProfile',
  'ChatScreen', 'Premium', 'Messages', 'Contacts', 'Home', 'VoiceChat',
  // PPL-26 — mediation route slots. Names match the app's RoomNavigator
  // stack screens (translated to web URLs via screenToPath below).
  'RoomStack',
  'RoomList',
  // PPL-450 — the rooms_list card's row target: the room's status + next-step
  // view, which forwards on by itself from whatever state the room is in.
  'RoomDetail',
  'RoomCreateInvite',
  'RoomInviteReceived',
  'RoomInviteByToken',
  'RoomPrep',
  'RoomConsent',
  'RoomSession',
  'RoomCheckin',
  'RoomEnd',
  'RoomSummary',
]);

export interface CardAction {
  action: 'navigate' | 'cloud_function' | 'clipboard' | 'skill_invoke';
  params?: Record<string, any>;
}

export type CardActionResult =
  | { type: 'skill_invoke'; message: string }
  | { type: 'navigate'; path: string; state?: Record<string, any> }
  | { type: 'cloud_function'; data: Record<string, any> }
  | null;

/**
 * Translate an app-screen + params object into a web URL.
 * Returns null if the screen has no web equivalent.
 */
function screenToPath(screen: string, params: Record<string, any> = {}): string | null {
  switch (screen) {
    case 'UserChatScreen':
    case 'UserChat':
    case 'Conversation': {
      const id = params.recipientId || params.userId || params.chatId;
      return id ? `/messages/${id}` : '/messages';
    }
    case 'Messages':
      return '/messages';
    case 'PublicUserProfile':
      return params.userId ? `/user/${params.userId}` : null;
    case 'CircleDetail':
      return params.circleId ? `/circles/${params.circleId}` : '/circles';
    case 'CircleChat':
      return params.circleId ? `/circles/${params.circleId}/chat` : null;
    case 'CircleEventDetail':
      if (params.circleId && params.eventId) {
        return `/circles/${params.circleId}/events/${params.eventId}`;
      }
      return null;
    case 'CircleEvents':
      return params.circleId ? `/circles/${params.circleId}/events` : null;
    case 'CirclesTab':
      return '/circles';
    case 'PplTalk':
      // Web's /talk uses messageSlug rather than postId — closest fit is the posts feed.
      return '/posts';
    case 'Insights':
      // The review queue shipped with PPL-265; this used to fall back to
      // /posts because web had no insights route at all.
      return '/insights';
    case 'UserProfile':
      return '/profile/edit';
    case 'ChatScreen':
      return '/f8chat';
    case 'Premium':
      return '/premium';
    case 'Contacts':
      return params.contactId ? `/contacts/${params.contactId}` : '/contacts';
    case 'Home':
      return '/';
    case 'VoiceChat':
      // Web has no standalone voice route; chat page launches voice mode internally.
      return '/f8chat';
    case 'RoomStack':
    case 'RoomList':
      return '/rooms';
    case 'RoomDetail':
      return params.mediationId ? `/rooms/${params.mediationId}` : '/rooms';
    case 'RoomCreateInvite':
      return '/rooms/new';
    case 'RoomInviteReceived':
      return params.mediationId ? `/rooms/${params.mediationId}/invite` : '/rooms';
    case 'RoomInviteByToken':
      return params.token ? `/i/${params.token}` : null;
    case 'RoomPrep':
      return params.mediationId ? `/rooms/${params.mediationId}/prep` : null;
    case 'RoomConsent':
      return params.mediationId ? `/rooms/${params.mediationId}/consent` : null;
    case 'RoomSession':
      return params.mediationId ? `/rooms/${params.mediationId}/room` : null;
    case 'RoomCheckin':
      return params.mediationId ? `/rooms/${params.mediationId}/checkin` : null;
    case 'RoomEnd':
      return params.mediationId ? `/rooms/${params.mediationId}/end` : null;
    case 'RoomSummary':
      return params.mediationId ? `/rooms/${params.mediationId}/summary` : null;
    default:
      return null;
  }
}

/**
 * Run a card action. Navigation is returned to the caller (which holds the
 * react-router navigator) rather than executed here.
 */
export async function handleCardAction(
  action: CardAction | null | undefined,
): Promise<CardActionResult> {
  if (!action) return null;

  switch (action.action) {
    case 'navigate': {
      const { screen, ...params } = action.params || {};
      if (!screen) return null;
      if (!ALLOWED_SCREENS.has(screen)) {
        console.warn(`[cardActionHandler] blocked navigation to: ${screen}`);
        return null;
      }
      const path = screenToPath(screen, params);
      if (!path) {
        console.warn(`[cardActionHandler] no web route for screen: ${screen}`);
        return null;
      }
      // Forward recipient context as router state so Messages can pre-fill
      // when the URL param doesn't match an existing conversation.
      const state: Record<string, any> = {};
      if (params.recipientId) state.recipientId = params.recipientId;
      if (params.recipientName) state.recipientName = params.recipientName;
      if (params.userId && screen !== 'PublicUserProfile') state.recipientId = params.userId;
      // Voice launcher card: F8Chat opens WebVoiceMode when it sees this flag.
      if (params.startVoice) state.startVoice = true;
      return Object.keys(state).length > 0
        ? { type: 'navigate', path, state }
        : { type: 'navigate', path };
    }

    case 'cloud_function': {
      const { functionName, args } = action.params || {};
      if (!functionName) return null;
      if (!ALLOWED_CLOUD_FUNCTIONS.has(functionName)) {
        console.warn(`[cardActionHandler] blocked function call: ${functionName}`);
        return null;
      }
      try {
        const fn = httpsCallable(FIREBASE_FUNCTIONS, functionName);
        const res = await fn(args || {});
        return { type: 'cloud_function', data: (res.data as Record<string, any>) ?? {} };
      } catch (err: any) {
        // Re-throw so callers that await onAction can catch and surface the error.
        throw err;
      }
    }

    case 'clipboard': {
      const text = action.params?.text;
      if (!text) return null;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for non-secure contexts
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
      } catch (err: any) {
        console.warn('[cardActionHandler] clipboard copy failed:', err?.message);
      }
      return null;
    }

    case 'skill_invoke': {
      const message = action.params?.message;
      if (typeof message === 'string' && message.length > 0) {
        return { type: 'skill_invoke', message };
      }
      return null;
    }

    default:
      return null;
  }
}
